import type { EventTypes } from '@racemap/sdk/schema/events';
import { uniq } from 'lodash';
import { useMemo } from 'react';
import type { RenderInfoBuffer } from 'src/lib/player/RenderInfoManager';
import { ALL_TAG, NULL_TAG } from 'src/lib/player/constants';
import type { EventState, PoiType, StarterType, TrackType } from 'src/lib/player/store';
import { type SideBarItem, SideBarItemType, SideBarSection, type StarterSorting } from './types';
import { deduplicateStarters, sortStarters } from './utils';

type UseSideBarItemsArgs = {
  searchText: string;
  openSections: Array<SideBarSection>;
  openStarterGroups: Array<string>;
  isFilterOpen: boolean;
  isSortOpen: boolean;
  starterSorting: StarterSorting | null;
  renderInfoBuffer: RenderInfoBuffer;
  showEventSelector: boolean;
  eventType: EventTypes;
  allEvents: Array<EventState>;
  allCurrentStarters: Array<StarterType>;
  allCurrentTracks: Array<TrackType>;
  allCurrentPois: Array<PoiType>;
  currentSelectedIds: Array<string>;
  availableTags: Map<string, Array<[string, number]>>;
  availableStarterGroups: Map<string, Array<[string, number]>>;
  filteredStarters: Array<StarterType>;
  deduplicatedStarters: Array<StarterType>;
};

export function useSideBarItems({
  searchText,
  openSections,
  openStarterGroups,
  isFilterOpen,
  isSortOpen,
  starterSorting,
  renderInfoBuffer,
  showEventSelector,
  eventType,
  allEvents,
  allCurrentStarters,
  allCurrentTracks,
  allCurrentPois,
  currentSelectedIds,
  availableTags,
  availableStarterGroups,
  filteredStarters,
  deduplicatedStarters,
}: UseSideBarItemsArgs): Array<SideBarItem> {
  return useMemo(() => {
    if (searchText === '') {
      const items: Array<SideBarItem> = [];
      items.push({
        type: SideBarItemType.HEADER,
        label: SideBarSection.FAVORITE,
      });
      if (openSections.includes(SideBarSection.FAVORITE)) {
        for (const track of allCurrentTracks.filter((poi) => currentSelectedIds.includes(poi.id))) {
          items.push({ type: SideBarItemType.TRACK, track, isFavorite: true });
        }
        for (const poi of allCurrentPois.filter((poi) => currentSelectedIds.includes(poi.id))) {
          items.push({ type: SideBarItemType.POI, poi, isFavorite: true });
        }
        for (const starter of deduplicateStarters(
          allCurrentStarters.filter((starter) => currentSelectedIds.includes(starter.id)),
        )) {
          items.push({ type: SideBarItemType.STARTER, starter, isFavorite: true });
        }
      }
      if (showEventSelector) {
        items.push({ type: SideBarItemType.HEADER, label: SideBarSection.EVENTS, eventType });
        if (openSections.includes(SideBarSection.EVENTS)) {
          for (const event of allEvents) {
            items.push({ type: SideBarItemType.EVENT, event });
          }
        }
      }
      if (allCurrentTracks.length > 0) {
        items.push({ type: SideBarItemType.HEADER, label: SideBarSection.TRACK });
        if (openSections.includes(SideBarSection.TRACK)) {
          for (const track of allCurrentTracks) {
            items.push({ type: SideBarItemType.TRACK, track });
          }
        }
      }
      if (allCurrentPois.length > 0) {
        items.push({ type: SideBarItemType.HEADER, label: SideBarSection.POI });
        if (openSections.includes(SideBarSection.POI)) {
          for (const poi of allCurrentPois) {
            items.push({ type: SideBarItemType.POI, poi });
          }
        }
      }
      if (allCurrentStarters.length > 0) {
        items.push({ type: SideBarItemType.HEADER, label: SideBarSection.STARTERS });
        if (openSections.includes(SideBarSection.STARTERS)) {
          if (isFilterOpen && availableTags.size > 0) {
            items.push({ type: SideBarItemType.STARTER_FILTER, itemCount: availableTags.size });
          }
          if (isSortOpen) {
            items.push({ type: SideBarItemType.STARTER_SORT });
          }
          if (availableStarterGroups.size > 0) {
            for (const [tagKey, tagValues] of availableStarterGroups) {
              for (const [tagValue] of tagValues) {
                items.push({ type: SideBarItemType.STARTER_GROUP, tagKey, tagValue });
                if (openStarterGroups.includes(`${tagKey}-${tagValue}`)) {
                  const startersInGroup = sortStarters(
                    deduplicatedStarters.filter(
                      (starter) =>
                        starter.tags.get(tagKey) === tagValue ||
                        (tagValue === NULL_TAG && !starter.tags.has(tagKey)),
                    ),
                    starterSorting,
                    renderInfoBuffer,
                  );
                  for (const starter of startersInGroup) {
                    items.push({
                      type: SideBarItemType.STARTER,
                      starter,
                      group: `${tagKey}-${tagValue}`,
                    });
                  }
                }
              }
            }
            items.push({ type: SideBarItemType.STARTER_GROUP, tagKey: ALL_TAG, tagValue: '' });
            if (openStarterGroups.includes(`${ALL_TAG}-`)) {
              for (const starter of sortStarters(
                deduplicatedStarters,
                starterSorting,
                renderInfoBuffer,
              )) {
                items.push({
                  type: SideBarItemType.STARTER,
                  starter,
                  group: `${ALL_TAG}-`,
                });
              }
            }
          } else {
            for (const starter of sortStarters(
              deduplicatedStarters,
              starterSorting,
              renderInfoBuffer,
            )) {
              items.push({ type: SideBarItemType.STARTER, starter });
            }
          }
        }
      }
      return items;
    }

    const wordsMatch = searchText.match(/\w+/gi);
    const words = wordsMatch != null ? wordsMatch.map((element) => element.toLowerCase()) : [];
    const pattern = `(${uniq(words).join('|')})`;
    const regexp = new RegExp(pattern, 'igm');

    const searchFilteredStarters = filteredStarters.filter(
      (starter) => starter.searchText.search(regexp) >= 0,
    );
    const filteredTracks = allCurrentTracks.filter((track) => track.name?.search(regexp) >= 0);
    const filteredPois = allCurrentPois.filter((poi) => poi.name?.search(regexp) >= 0);

    const items: Array<SideBarItem> = [];

    if (filteredTracks.length > 0) {
      items.push({ type: SideBarItemType.HEADER, label: SideBarSection.TRACK });
    }
    for (const track of filteredTracks) {
      items.push({ type: SideBarItemType.TRACK, track });
    }

    if (filteredPois.length > 0) {
      items.push({ type: SideBarItemType.HEADER, label: SideBarSection.POI });
    }
    for (const poi of filteredPois) {
      items.push({ type: SideBarItemType.POI, poi });
    }

    if (searchFilteredStarters.length > 0) {
      items.push({ type: SideBarItemType.HEADER, label: SideBarSection.STARTERS });
    }
    if (isFilterOpen && availableTags.size > 0) {
      items.push({ type: SideBarItemType.STARTER_FILTER, itemCount: availableTags.size });
    }
    if (isSortOpen) {
      items.push({ type: SideBarItemType.STARTER_SORT });
    }
    if (openSections.includes(SideBarSection.STARTERS)) {
      for (const starter of deduplicateStarters(searchFilteredStarters)) {
        items.push({ type: SideBarItemType.STARTER, starter });
      }
    }

    return items;
  }, [
    searchText,
    openSections,
    openStarterGroups,
    isFilterOpen,
    isSortOpen,
    starterSorting,
    renderInfoBuffer,
    showEventSelector,
    eventType,
    allEvents,
    allCurrentStarters,
    allCurrentTracks,
    allCurrentPois,
    currentSelectedIds,
    availableTags,
    availableStarterGroups,
    filteredStarters,
    deduplicatedStarters,
  ]);
}
